import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNoticeStore } from '../../../stores/noticeStore';
import { noticeEventBus } from '../lib/noticeEventBus';
import { getWebSocketClient } from '../../../lib/websocket/boardWebSocket';
import type { NoticeRecord } from '../../../lib/api/notices';

/**
 * Notice shape consumed by the StackedNoticeBanners UI component.
 * This interface is unchanged — the UI component stays the same.
 */
export interface Notice {
  id: string;
  text: string;
  type: 'default' | 'success' | 'syncing' | 'ai-processing' | 'error' | 'warning';
  imageUrl?: string;
  sticky?: boolean;
  actionType?: string;
  actionPayload?: any;
  metadata?: any;
  onAction?: () => void;
}

/**
 * Map a backend NoticeRecord to the UI Notice shape.
 */
function mapRecordToNotice(record: NoticeRecord, t: any, onClickAction?: (record: NoticeRecord) => void): Notice {
  // Derive the visual type from loading/state/title/action fields
  let type: Notice['type'] = 'default';
  const stateL = record.state?.toLowerCase() ?? '';
  const titleL = record.title?.toLowerCase() ?? '';
  const actionType = record.payload?.actionType ?? '';

  if (record.loading) {
    type = 'syncing';
  } else if (stateL.includes('fail') || stateL.includes('error') || titleL.includes('fail') || titleL.includes('error') || actionType === 'OPEN_SYNC_ERROR_MODAL') {
    type = 'error';
  } else if (stateL.includes('finish') || stateL.includes('done') || stateL === 'ready' || titleL.includes('success') || actionType === 'OPEN_SYNC_COMPLETE_MODAL') {
    type = 'success';
  }

  const translatedState = record.state && (record.state.startsWith('sync.') || record.state.startsWith('notices.'))
    ? t(`common:${record.state}`, record.payload) 
    : record.state;

  const translatedTitle = record.title && record.title.startsWith('notices.')
    ? t(`common:${record.title}`, record.payload)
    : record.title;

  // Compose the display text: "translatedTitle   translatedState"
  const displayText = translatedState
    ? `${translatedTitle}  ·  ${translatedState}`
    : translatedTitle;

  return {
    id: record.refKey, // Use refKey as the stable identifier
    text: displayText,
    type,
    imageUrl: record.payload?.imageUrl,
    sticky: record.sticky,
    actionPayload: record.payload,
    onAction: onClickAction ? () => onClickAction(record) : undefined,
  };
}

/**
 * useBoardNotices — Connects the notice store, event bus, and WebSocket
 * to the StackedNoticeBanners UI component.
 *
 * This hook:
 * 1. Hydrates notices from the server on mount
 * 2. Registers default event bus handlers (AI task notices)
 * 3. Listens for WebSocket notice:upserted / notice:dismissed events
 * 4. Maps NoticeRecord[] → Notice[] for the UI
 */
export const useBoardNotices = (
  boardId: string,
  _options: {
    onOpenChannelSource: (channelId?: string) => void;
    onSyncComplete: (channelId?: string) => void;
    onSyncError: (channelId?: string, errorMessage?: string) => void;
    onAITaskComplete?: (payload: any) => void;
    suppressAutoOpen?: boolean;
  },
) => {
  const { t } = useTranslation(['common']);
  const [isStackedNoticesOpen, setIsStackedNoticesOpen] = useState(false);
  const [activeNoticeIndex, setActiveNoticeIndex] = useState<number | null>(null);
  const [isNoticeBarOpen, setIsNoticeBarOpen] = useState(false);

  const {
    notices: noticeRecords,
    isHydrated,
    hydrateFromServer,
    dismissNotice,
    applyRemoteUpsert,
    applyRemoteDismissal,
    clearAll,
  } = useNoticeStore();

  // Ref to avoid stale closures in callbacks
  const boardIdRef = useRef(boardId);
  boardIdRef.current = boardId;

  const suppressRef = useRef(_options.suppressAutoOpen ?? false);
  suppressRef.current = _options.suppressAutoOpen ?? false;

  // ─── 1. Hydrate on mount ───────────────────────────────

  useEffect(() => {
    if (boardId) {
      hydrateFromServer(boardId);
    }

    return () => {
      clearAll();
    };
  }, [boardId]);

  // ─── 2. WebSocket listeners ────────────────────────────

  useEffect(() => {
    try {
      const ws = getWebSocketClient();

      const handleUpsert = (data: any) => {
        // Only apply if it's for the current board or a global notice
        if (data.boardId && data.boardId !== boardIdRef.current) return;
        applyRemoteUpsert(data as NoticeRecord);

        // Auto-open the notices panel when a new notice arrives (unless modals are blocking)
        // CRITICAL: Always auto-open for loading (syncing) notices even if suppressed
        if (!suppressRef.current || data.loading) {
          setIsStackedNoticesOpen(true);
        }
      };

      const handleDismissal = (data: any) => {
        applyRemoteDismissal(data.refKey);
      };

      ws.on('notice:upserted', handleUpsert);
      ws.on('notice:dismissed', handleDismissal);

      return () => {
        ws.off('notice:upserted', handleUpsert);
        ws.off('notice:dismissed', handleDismissal);
      };
    } catch (_) {
      // WebSocket not initialized yet — will work after reconnect
    }
  }, [boardId]);

  // ─── 3. Notice click handler ───────────────────────────

  const handleNoticeClick = useCallback(
    (record: NoticeRecord) => {
      // Fire a click event through the bus for external listeners
      noticeEventBus.fire({
        type: 'DISMISS_NOTICE',
        payload: { refKey: record.refKey, notice: record },
      });

      // Execute custom actions based on payload
      if (record.payload?.actionType === 'OPEN_SELECT_CHANNEL_MODAL' && _options?.onOpenChannelSource) {
        _options.onOpenChannelSource(record.payload?.channelId);
      } else if (record.payload?.actionType === 'OPEN_SYNC_COMPLETE_MODAL' && _options?.onSyncComplete) {
        _options.onSyncComplete(record.payload?.channelId);
      } else if (record.payload?.actionType === 'OPEN_SYNC_ERROR_MODAL' && _options?.onSyncError) {
        _options.onSyncError(record.payload?.channelId, record.payload?.errorMessage);
      } else if (record.payload?.actionType === 'OPEN_AI_TASK_RESULT' && _options?.onAITaskComplete) {
        _options.onAITaskComplete(record.payload);
      }

      // If non-sticky, dismiss it
      if (!record.sticky && boardIdRef.current) {
        dismissNotice(boardIdRef.current, record.refKey);
      }
    },
    [dismissNotice, _options],
  );

  // ─── 4. Default NoticeEventBus Handlers ──────────────────

  useEffect(() => {
    const unsubUpsert = noticeEventBus.subscribe('GENERIC_NOTICE_SHOW', (event) => {
      if (boardIdRef.current) {
        useNoticeStore.getState().upsertNotice(boardIdRef.current, {
           refKey: event.payload.refKey,
           title: event.payload.title,
           state: event.payload.state,
           loading: event.payload.loading,
           sticky: event.payload.sticky,
           payload: event.payload.payload
        });
      }
    });

    const unsubDismiss = noticeEventBus.subscribe('DISMISS_NOTICE', (event) => {
      // Avoid looping if the click handler fired this
      if (!event.payload.notice && boardIdRef.current) {
        useNoticeStore.getState().dismissNotice(boardIdRef.current, event.payload.refKey);
      }
    });

    return () => {
      unsubUpsert();
      unsubDismiss();
    };
  }, []);

  // ─── 4. Map records → UI Notices ───────────────────────

  const notices: Notice[] = useMemo(
    () => noticeRecords.map(record => mapRecordToNotice(record, t, handleNoticeClick)),
    [noticeRecords, t, handleNoticeClick]
  );

  // Auto-open when notices arrive (unless modals are blocking)
  useEffect(() => {
    if (notices.length > 0 && isHydrated && !_options.suppressAutoOpen) {
      setIsStackedNoticesOpen(true);
    }
  }, [notices.length, isHydrated, _options.suppressAutoOpen]);

  // Close notices when suppression kicks in (modal opened)
  useEffect(() => {
    if (_options.suppressAutoOpen) {
      setIsStackedNoticesOpen(false);
    }
  }, [_options.suppressAutoOpen]);

  // ─── 5. Auto-dismiss transient notices ──────────────────
  // Non-sticky success/error notices auto-dismiss after a delay
  useEffect(() => {
    const transient = noticeRecords.filter(r => !r.sticky && !r.loading);
    if (transient.length === 0) return;

    const timers = transient.map(record => {
      const isError = (record.state?.toLowerCase().includes('error') || record.state?.toLowerCase().includes('fail') || record.payload?.actionType === 'OPEN_SYNC_ERROR_MODAL');
      const delay = isError ? 12000 : 8000; // Errors linger longer
      return setTimeout(() => {
        if (boardIdRef.current) {
          dismissNotice(boardIdRef.current, record.refKey);
        }
      }, delay);
    });

    return () => timers.forEach(clearTimeout);
  }, [noticeRecords, dismissNotice]);

  // ─── 6. Delete handler for UI ──────────────────────────

  const removeNotice = useCallback(
    (index: number) => {
      const notice = noticeRecords[index];
      if (notice && boardIdRef.current) {
        dismissNotice(boardIdRef.current, notice.refKey);
      }
    },
    [noticeRecords, dismissNotice],
  );

  return {
    notices,
    removeNotice,
    isStackedNoticesOpen,
    setIsStackedNoticesOpen,
    activeNoticeIndex,
    setActiveNoticeIndex,
    isNoticeBarOpen,
    setIsNoticeBarOpen,
  };
};
